import { router } from "@inertiajs/react"
import { useEffect } from "react"

import { getCableConsumer } from "@/lib/cable"

const ACTIVITY_RELOAD_PROPS = ["activity_feed", "unread_notifications_count"]

interface RefetchSignal {
  type?: string
  reload?: unknown
}

/**
 * OKS-548 — Activity-feed live new-arrival hook (creator-only).
 *
 * Mounted on `Creator/Activity/Index`. Subscribes to `CreatorChannel`
 * (the same lightweight OKS-406 refetch signal the shell already uses)
 * and, on ANY `refetch` signal, partial-reloads the `activity_feed` +
 * `unread_notifications_count` props. The signal carries NO payload and
 * NO brand data (TRD §6) — the reload recomputes both props server-side
 * in the creator's active-brand context, so isolation stays server-side.
 *
 * The reload REPLACES `activity_feed` with the canonical newest-first
 * window; the page diffs incoming ids against its mount-time `seenIds`
 * set to flag which rows are new-this-session (NEW pill / NOW / pulse).
 * Idempotent with the shell's `useRealtimeRefetch` + poll — Inertia
 * replaces (never increments) the count, so concurrent signals can't
 * double-count.
 *
 * Degrades silently when the WebSocket transport is unavailable (real-
 * time is an enhancement; a full visit still shows every row).
 */
export function useActivityLive() {
  useEffect(() => {
    let consumer
    try {
      consumer = getCableConsumer()
    } catch {
      return
    }

    const subscription = consumer.subscriptions.create(
      { channel: "CreatorChannel" },
      {
        received(data: RefetchSignal) {
          if (data?.type !== "refetch") return
          router.reload({ only: ACTIVITY_RELOAD_PROPS, preserveUrl: true })
        },
      },
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [])
}
